import { Component, type ReactNode } from "react";
import { reportClientError } from "@/lib/errorReporter";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  /** Erro de chunk/storage com recarga automática pendente: mostra só um splash, sem o cartão de erro. */
  recovering?: boolean;
}

// Depois de cada deploy, os arquivos JS de cada página ganham um hash novo (ex: page-DcXDvFWW.js).
// Uma aba que já estava aberta antes do deploy ainda tenta buscar o hash antigo ao navegar pra uma
// página lazy-loaded — o arquivo não existe mais no servidor, e o import() falha. Detectamos essa
// falha específica pela mensagem do erro e recarregamos a página automaticamente.
function isChunkLoadError(message: string): boolean {
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|_result\.default|reading 'default'/i.test(message);
}

// Bug real corrigido 06/09/2026: acesso cru a localStorage/sessionStorage
// (fora de lib/api.ts, que já tinha proteção desde 27/08) em ~30 arquivos
// derrubava a página inteira ao trocar de aba/página em qualquer navegador
// que restrinja storage no momento (battery saver, modo privado, quota
// cheia, MDM). Rede de segurança: qualquer erro dessa classe também recarrega
// sozinho, mesmo padrão do chunk error — cobre pontos que a correção direta
// em `lib/safeStorage.ts` ainda não alcançou.
function isStorageError(message: string): boolean {
  return /localStorage|sessionStorage|Access is denied for this document|QuotaExceededError/i.test(message);
}

const RELOAD_FLAG_KEY = "clinica_chunk_reload_attempted";

/**
 * Recarrega a página PULANDO o cache HTTP: um `window.location.reload()` comum
 * pode reaproveitar o index.html antigo que o navegador tem em cache (e que
 * ainda referencia os hashes velhos dos chunks), fazendo o erro voltar logo em
 * seguida — em loop. Anexar um query único à URL força o navegador a baixar o
 * index.html NOVO do último deploy. Usamos location.replace() para não poluir o
 * histórico (o usuário não volta para a página quebrada com o botão voltar).
 */
function hardReload() {
  const { pathname, search, hash } = window.location;
  const sep = search ? "&" : "?";
  window.location.replace(`${pathname}${search}${sep}_cln_reload=${Date.now()}${hash}`);
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: Error): State {
    const msg = error.message || "Erro inesperado";
    let alreadyTried = false;
    try { alreadyTried = !!sessionStorage.getItem(RELOAD_FLAG_KEY); } catch { /* storage bloqueado */ }
    const recovering = (isChunkLoadError(msg) || isStorageError(msg)) && !alreadyTried;
    return { hasError: true, errorMessage: msg, recovering };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error("[ErrorBoundary]", error.message, info.componentStack);
    // Tela de erro do React: registra no servidor (01/11/2026) com a pilha do componente.
    reportClientError("react", error.message || "Erro na tela", { stack: `${error.stack || ""}\n--- componentes ---${String(info.componentStack || "").slice(0, 800)}` });

    if (isChunkLoadError(error.message) || isStorageError(error.message)) {
      // sessionStorage também pode estar bloqueado no mesmo cenário (storage
      // hostil) — sem esse try/catch aqui, a própria checagem da flag lançaria
      // de novo e impediria o reload de acontecer.
      let alreadyTried = false;
      try { alreadyTried = !!sessionStorage.getItem(RELOAD_FLAG_KEY); } catch { /* segue como false */ }
      if (!alreadyTried) {
        try { sessionStorage.setItem(RELOAD_FLAG_KEY, "1"); } catch { /* storage bloqueado — tenta reload mesmo assim */ }
        hardReload();
      }
    }
  }

  // Só roda quando a árvore montou SEM erro — ou seja, após um reload bem-
  // sucedido. Antes a flag era limpa apenas em componentDidUpdate, que NÃO roda
  // na montagem inicial: depois do primeiro auto-reload ela ficava presa em
  // sessionStorage pelo resto da sessão e um SEGUNDO erro de chunk na mesma aba
  // não se recuperava mais sozinho ("Algo deu errado" aparecia e ficava).
  // A guarda !hasError garante que, se o erro acontecer logo no primeiro render,
  // a flag recém-gravada não seja apagada antes do reload acontecer.
  componentDidMount() {
    if (!this.state.hasError) {
      try { sessionStorage.removeItem(RELOAD_FLAG_KEY); } catch { /* storage bloqueado — sem efeito prático aqui */ }
    }
  }

  componentDidUpdate() {
    // Reload bem-sucedido — limpa a flag pra permitir recuperação automática
    // de novo no futuro (defesa extra; o caso principal é tratado no mount).
    if (!this.state.hasError) {
      try { sessionStorage.removeItem(RELOAD_FLAG_KEY); } catch { /* storage bloqueado — sem efeito prático aqui */ }
    }
  }

  render() {
    if (this.state.hasError) {
      // A recarga automática já foi disparada: não pisca o cartão de erro, só um splash neutro.
      if (this.state.recovering) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <i className="ri-loader-4-line text-3xl text-emerald-500 animate-spin" />
          </div>
        );
      }
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
          <div className="text-center max-w-sm bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-red-500" />
            </div>
            <h1 className="text-lg font-bold text-slate-800 mb-2">Algo deu errado</h1>
            <p className="text-sm text-slate-500 mb-1">
              Ocorreu um erro inesperado ao carregar esta página.
            </p>
            <p className="text-xs text-slate-400 mb-6 font-mono bg-slate-50 rounded-lg px-3 py-2 break-all">
              {this.state.errorMessage}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, errorMessage: "" });
                hardReload();
              }}
              className="px-5 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1.5" />
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
